// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useAlerts } from '../context/AlertsContext';

const TYPE_ICON = {
  stargate_offline: SignalWifiOffIcon,
  stargate_online: CheckCircleOutlineIcon,
  stargate_recovered: CheckCircleOutlineIcon,
  data_gap: TimelineIcon,
  mqtt_disconnected: LinkOffIcon,
  firmware_update: CloudUploadIcon,
};

const SEVERITY = {
  critical: { color: '#ef5350', dim: 'rgba(239,83,80,0.12)' },
  warning: { color: '#ffa726', dim: 'rgba(255,167,38,0.10)' },
  success: { color: '#66bb6a', dim: 'rgba(102,187,106,0.12)' },
  info: { color: '#29b6f6', dim: 'rgba(41,182,246,0.08)' },
};

function topSeverity(alerts) {
  if (alerts.some((a) => a.severity === 'critical')) return 'critical';
  if (alerts.some((a) => a.severity === 'warning')) return 'warning';
  if (alerts.some((a) => a.severity === 'success')) return 'success';
  return 'info';
}

// Deep link into Network Metrics zoomed to the alert's gap window.
function gapChartLink(alert) {
  if (alert.type !== 'data_gap' || !alert.deviceSerial || !alert.gapStart) return null;
  const params = new URLSearchParams();
  params.set('device', alert.deviceSerial);
  if (alert.gapMetricType) params.set('metric', alert.gapMetricType);
  params.set('from', String(alert.gapStart));
  params.set('to', String(alert.gapEnd || Date.now()));
  return `/network-metrics?${params.toString()}`;
}

function isGapAlert(alert) {
  return alert.type === 'data_gap' && Boolean(alert.gapIds?.length);
}

export default function AlertsBanner() {
  const {
    alerts,
    bannerVisible,
    hideBanner,
    dismissAlert,
    permanentlyDismissAlert,
    acknowledgeGapAlert,
  } = useAlerts();
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  // Collapse the list when there's nothing to expand, so it doesn't
  // surprise-reopen the next time a second alert arrives.
  useEffect(() => {
    if (alerts.length <= 1) setExpanded(false);
  }, [alerts.length]);

  if (!bannerVisible || alerts.length === 0) return null;

  // Dismissal scope must match how the alert id is scoped:
  // - gap alerts acknowledge server-side (shared truth, all users)
  // - incident alerts (id embeds a timestamp) may be hidden permanently —
  //   a future incident gets a fresh id and still shows
  // - standing-condition alerts (stable class ids: firmware, registry, token
  //   expiry) only session-dismiss, or one click would silently suppress
  //   every future occurrence forever on this browser
  const handleTrash = (alert) => {
    if (isGapAlert(alert)) acknowledgeGapAlert(alert);
    else if (alert.incident) permanentlyDismissAlert(alert.id);
    else dismissAlert(alert.id);
  };
  const trashTooltip = (alert) =>
    isGapAlert(alert)
      ? 'Acknowledge — clears for all users'
      : alert.incident
        ? 'Dismiss permanently'
        : 'Dismiss for this session';
  const openGapChart = (alert) => {
    const link = gapChartLink(alert);
    if (link) navigate(link);
  };

  const sev = topSeverity(alerts);
  const s = SEVERITY[sev];
  const single = alerts.length === 1;
  const SevIcon =
    sev === 'critical'
      ? ErrorOutlineIcon
      : sev === 'success'
        ? CheckCircleOutlineIcon
        : WarningAmberIcon;

  const summaryText = single
    ? `${alerts[0].title}: ${alerts[0].message}`
    : `${alerts.length} active alerts`;
  const singleGapLink = single && gapChartLink(alerts[0]);

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      {/* ─── Summary bar ─── */}
      <Box
        onClick={() => {
          if (single) openGapChart(alerts[0]);
          else setExpanded((e) => !e);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: '6px',
          bgcolor: s.dim,
          borderLeft: `3px solid ${s.color}`,
          cursor: single && !singleGapLink ? 'default' : 'pointer',
          userSelect: 'none',
          minHeight: 32,
        }}
      >
        <SevIcon sx={{ fontSize: 16, color: s.color, flexShrink: 0 }} />

        <Typography
          variant="body2"
          sx={{
            color: s.color,
            fontWeight: 600,
            fontSize: '0.8rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summaryText}
        </Typography>

        {!single &&
          (expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          ))}

        {/* Dismiss this notification */}
        {single && (
          <Tooltip title={trashTooltip(alerts[0])} arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleTrash(alerts[0]);
              }}
              sx={{
                p: 0.25,
                color: 'text.disabled',
                '&:hover': { color: s.color },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* X hides the banner — alerts remain active, bell icon brings it back */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            hideBanner();
          }}
          sx={{
            p: 0.25,
            color: 'text.disabled',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* ─── Expanded list (scrollable when 8+ rows) ─── */}
      <Collapse in={expanded && !single} timeout={150}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            mt: '4px',
            pl: 1,
            ...(alerts.length >= 8 && {
              maxHeight: 230,
              overflowY: 'auto',
            }),
          }}
        >
          {alerts.map((alert) => {
            const as = SEVERITY[alert.severity] || SEVERITY.info;
            const Icon = TYPE_ICON[alert.type] || TimelineIcon;
            const clickable = Boolean(gapChartLink(alert));
            return (
              <Box
                key={alert.id}
                onClick={clickable ? () => openGapChart(alert) : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: '4px',
                  bgcolor: as.dim,
                  borderLeft: `2px solid ${as.color}`,
                  minHeight: 26,
                  ...(clickable && {
                    cursor: 'pointer',
                    '&:hover': { filter: 'brightness(1.25)' },
                  }),
                }}
              >
                <Icon sx={{ fontSize: 14, color: as.color, flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: as.color,
                    fontWeight: 600,
                    // Ellipsis only on phones; sm+ keeps prior non-shrinking title.
                    flexShrink: { xs: 1, sm: 0 },
                    minWidth: { xs: 0, sm: 'auto' },
                    maxWidth: { xs: '40%', sm: 'none' },
                    overflow: { xs: 'hidden', sm: 'visible' },
                    textOverflow: { xs: 'ellipsis', sm: 'clip' },
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                  }}
                >
                  {alert.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                  }}
                >
                  {alert.message}
                </Typography>
                <Tooltip title={trashTooltip(alert)} arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrash(alert);
                    }}
                    sx={{
                      p: 0.25,
                      ml: 0.5,
                      color: 'text.disabled',
                      '&:hover': { color: as.color },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
